// -----------------------------------------------------------------------------
// ui/panels/property.ts — the "Adjust" panel (colour grade / opacity / fades).
//
// Context-sensitive: when a video or image clip is selected it shows filter
// presets plus sliders for brightness/contrast/saturation/temperature, opacity
// and fade in/out. Edits preview live (direct mutation + notify) and commit a
// single undoable UpdateClipCommand on release — same pattern as the text panel.
// -----------------------------------------------------------------------------
import { type ClipPropPatch, UpdateClipCommand } from '../../core/commands/clip-commands';
import { type ColorAdjust, type MediaClip, NEUTRAL_ADJUST, locateClip } from '../../core/model';
import type { EditorContext } from '../context';
import { clear, el } from '../dom';

const FILTERS: Array<{ name: string; adjust: ColorAdjust }> = [
  { name: 'None', adjust: NEUTRAL_ADJUST },
  { name: 'Vivid', adjust: { brightness: 0, contrast: 0.15, saturation: 0.4, temperature: 0 } },
  { name: 'Warm', adjust: { brightness: 0.03, contrast: 0.05, saturation: 0.1, temperature: 0.35 } },
  { name: 'Cool', adjust: { brightness: 0, contrast: 0.05, saturation: 0.05, temperature: -0.35 } },
  { name: 'Retro', adjust: { brightness: 0.03, contrast: 0.1, saturation: -0.25, temperature: 0.25 } },
  { name: 'Mono', adjust: { brightness: 0.02, contrast: 0.12, saturation: -1, temperature: 0 } },
];

export function createPropertyPanel(ctx: EditorContext): HTMLElement {
  const { store } = ctx;
  const body = el('div', {});
  const panel = el(
    'div',
    { class: 'zf-panel' },
    el('h2', {}, 'Adjust'),
    el('p', { class: 'zf-panel-hint' }, 'Colour, opacity and fades for the selected video or image.'),
    body,
  );

  let lastSelection: string | null | undefined;
  render();
  store.subscribe(() => {
    if (store.state.selectedClipId !== lastSelection) render();
  });

  function selectedMedia(): MediaClip | null {
    const id = store.state.selectedClipId;
    if (!id) return null;
    const loc = locateClip(store.state.project, id);
    return loc && (loc.clip.kind === 'video' || loc.clip.kind === 'image') ? loc.clip : null;
  }

  function render(): void {
    lastSelection = store.state.selectedClipId;
    clear(body);
    const clip = selectedMedia();
    if (!clip) {
      body.append(el('p', { class: 'zf-panel-hint' }, 'Select a video or image clip.'));
      return;
    }

    const filterRow = el('div', { class: 'zf-filter-row' });
    for (const f of FILTERS) {
      filterRow.append(
        el(
          'button',
          { class: 'zf-btn zf-btn-ghost zf-filter', onclick: () => applyFilter(clip.id, f.adjust) },
          f.name,
        ),
      );
    }

    const adj = clip.adjust ?? NEUTRAL_ADJUST;
    const maxFade = Math.max(5, clip.durationSec);
    body.append(
      field('Filters', filterRow),
      slider('Brightness', clip.id, 'adjust.brightness', adj.brightness, -1, 1),
      slider('Contrast', clip.id, 'adjust.contrast', adj.contrast, -1, 1),
      slider('Saturation', clip.id, 'adjust.saturation', adj.saturation, -1, 1),
      slider('Temperature', clip.id, 'adjust.temperature', adj.temperature, -1, 1),
      slider('Opacity', clip.id, 'opacity', clip.opacity ?? 1, 0, 1),
      el(
        'div',
        { class: 'zf-row' },
        slider('Fade in (s)', clip.id, 'fadeInSec', clip.fadeInSec ?? 0, 0, maxFade),
        slider('Fade out (s)', clip.id, 'fadeOutSec', clip.fadeOutSec ?? 0, 0, maxFade),
      ),
    );
  }

  function applyFilter(clipId: string, adjust: ColorAdjust): void {
    store.dispatch(new UpdateClipCommand(clipId, { adjust: { ...adjust } }));
    ctx.setStatus('Filter applied.');
  }

  function slider(label: string, clipId: string, path: string, value: number, min: number, max: number): HTMLElement {
    const input = el('input', {
      type: 'range',
      min: String(min),
      max: String(max),
      step: String((max - min) / 100),
      value: String(value),
    }) as HTMLInputElement;
    bindClipProp(input, clipId, path);
    return field(label, input);
  }

  // Live-preview while dragging; one undoable command on release.
  function bindClipProp(input: HTMLInputElement, clipId: string, path: string): void {
    const isAdjust = path.startsWith('adjust.');
    const key = isAdjust ? path.slice(7) : path;
    let original: number | undefined;

    const getClip = (): MediaClip | null => {
      const loc = locateClip(store.state.project, clipId);
      return loc && loc.clip.kind !== 'text' ? loc.clip : null;
    };
    const read = (clip: MediaClip): number => {
      if (isAdjust) return (clip.adjust ?? NEUTRAL_ADJUST)[key as keyof ColorAdjust];
      return (clip as unknown as Record<string, number | undefined>)[key] ?? (key === 'opacity' ? 1 : 0);
    };
    const write = (clip: MediaClip, val: number): void => {
      if (isAdjust) {
        const next: ColorAdjust = { ...(clip.adjust ?? NEUTRAL_ADJUST) };
        (next as unknown as Record<string, number>)[key] = val;
        clip.adjust = next;
      } else {
        (clip as unknown as Record<string, number>)[key] = val;
      }
    };

    input.addEventListener('input', () => {
      const clip = getClip();
      if (!clip) return;
      if (original === undefined) original = read(clip);
      write(clip, Number(input.value));
      store.notify();
    });
    input.addEventListener('change', () => {
      const clip = getClip();
      if (!clip) return;
      const value = read(clip);
      if (original === undefined) original = value;
      if (Object.is(value, original)) {
        original = undefined;
        return;
      }
      write(clip, original); // restore, then commit one undoable command
      const patch: ClipPropPatch = {};
      if (isAdjust) {
        const next: ColorAdjust = { ...(clip.adjust ?? NEUTRAL_ADJUST) };
        (next as unknown as Record<string, number>)[key] = value;
        patch.adjust = next;
      } else {
        (patch as unknown as Record<string, number>)[key] = value;
      }
      store.dispatch(new UpdateClipCommand(clipId, patch));
      original = undefined;
    });
  }

  return panel;
}

function field(label: string, control: HTMLElement): HTMLElement {
  return el('label', { class: 'zf-field' }, el('span', {}, label), control);
}
